/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the administer pharmacy page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { useParams } from 'react-router';

import { Theme, makeStyles } from '@material-ui/core/styles';

/**
 * Used to format text.
 */
import Typography from '@material-ui/core/Typography';

import { RequestState } from '@ngt/request-utilities';

import { Button, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

import { useBctApiPermissions } from '@ngt/online-patient-management-bctapi-permissions';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { CollapsibleTable, useInstitutions } from '@ngt/online-patient-management-ui';
import * as Dtos from '../../api/Neon.Dtos';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import PharmacyInformation from '../../components/PharmacyInformation/PharmacyInformation';
import drugManagementSettings from '../../constants/drugManagementSettings';
import { useDepots } from '../../store/modules/drugmanagement/depots';
import { useDrugs } from '../../store/modules/drugmanagement/drugs';
import { useDrugShipmentDepotBatch } from '../../store/modules/drugmanagement/drugshipmentdepotbatch';
import { useDrugShipmentDepotBatches } from '../../store/modules/drugmanagement/drugshipmentdepotbatches';
import { usePharmacies } from '../../store/modules/drugmanagement/pharmacies';
import { usePharmacists } from '../../store/modules/drugmanagement/pharmacists';
import { useActionQuarantinedPharmacyStock, usePharmacy, useQuarantinePharmacyStock } from '../../store/modules/drugmanagement/pharmacy';
import { IDrugShipmentDepotBatchSummary, createDrugShipmentDepotBatchSummary, getDrugRemaining, sumDrugShipmentDepotBatches } from '../../utilities/drugShipmentDepotBatchCalculators';
import DrugShipmentDepotBatchModal from './Modals/DrugShipmentDepotBatchModal';
import EditQuarantinedStockModal from './Modals/EditQuarantinedStockModal';
import PharmacyModal from './Modals/PharmacyModal';
import QuarantinePharmacyStockModal from './Modals/QuarantinePharmacyStockModal';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IAdministerPharmacyParams {
    pharmacyId?: string;
}

interface IAdministerPharmacyProps {
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    heading: {
        marginTop: theme.spacing(4)
    },
    buttonSet: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(1),
        textAlign: 'right'
    },
    button: {
        marginLeft: theme.spacing(3),

        '&:first-child': {
            marginLeft: theme.spacing(0)
        }
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

const drugManagementPermissions = [Dtos.Permission.DrugManagementAdminister];

const useAvailableDepotBatchColumns = (drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DrugShipmentDepotBatch>> = [
            {
                Header: drugManagementSettings.consignmentNumberLabel,
                id: 'consignmentNo',
                accessor: (d) => d.drugShipment.consignmentNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Depot Batch Number',
                id: 'depotBatchNo',
                accessor: (d) => d.depotBatch.batch.batchNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Drug',
                id: 'drug',
                accessor: (d) => d.depotBatch.batch.drug.drugName,
                minWidth: 125,
                maxWidth: 125
            },
            {
                Header: 'Total',
                accessor: 'drugUnits',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Used',
                accessor: 'drugUnitsUsed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Damaged',
                accessor: 'drugUnitsDamaged',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Destroyed',
                accessor: 'drugUnitsDestroyed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Lost',
                accessor: 'drugUnitsLost',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Quarantined',
                accessor: 'drugUnitsQuarantined',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Available',
                id: 'available',
                accessor: (dsb) => getDrugRemaining(dsb),
                minWidth: 155,
                maxWidth: 155
            },
        ];

        return cols;
    }, []);

    return columns;
};

const useSummedAvailableDepotBatchColumns = (drugShipmentDepotBatches: IDrugShipmentDepotBatchSummary[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<IDrugShipmentDepotBatchSummary>> = [
            {
                Header: 'Batch Number',
                accessor: (d) => d.batch.batchNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Drug',
                id: 'drug',
                accessor: (d) => d.batch.drug.drugName,
                minWidth: 125,
                maxWidth: 125
            },
            {
                Header: 'Total',
                accessor: 'drugUnits',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Used',
                accessor: 'drugUnitsUsed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Damaged',
                accessor: 'drugUnitsDamaged',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Destroyed',
                accessor: 'drugUnitsDestroyed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Lost',
                accessor: 'drugUnitsLost',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Quarantined',
                accessor: 'drugUnitsQuarantined',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Available',
                accessor: 'drugRemaining',
                minWidth: 155,
                maxWidth: 155
            },
        ];

        return cols;
    }, []);

    return columns;
};

const useQuarantinedAtSiteDepotBatchColumns = (drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DrugShipmentDepotBatch>> = [
            {
                Header: drugManagementSettings.consignmentNumberLabel,
                id: 'consignmentNo',
                accessor: (d) => d.drugShipment.consignmentNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Depot Batch Number',
                id: 'depotBatchNo',
                accessor: (d) => d.depotBatch.batch.batchNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Drug',
                id: 'drug',
                accessor: (d) => d.depotBatch.batch.drug.drugName,
                minWidth: 125,
                maxWidth: 125
            },
            {
                Header: 'Total',
                accessor: 'drugUnits',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Used',
                accessor: 'drugUnitsUsed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Damaged',
                accessor: 'drugUnitsDamaged',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Destroyed',
                accessor: 'drugUnitsDestroyed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Lost',
                accessor: 'drugUnitsLost',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Quarantined',
                id: 'quarantined',
                accessor: (dsb) => <Typography color="error" variant="inherit"><strong>{dsb?.drugUnitsQuarantined ?? 0}</strong></Typography>,
                minWidth: 155,
                maxWidth: 155
            },
        ];

        return cols;
    }, []);

    return columns;
};

const useDestroyedDepotBatchColumns = (drugShipmentDepotBatches: Dtos.DrugShipmentDepotBatch[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.DrugShipmentDepotBatch>> = [
            {
                Header: drugManagementSettings.consignmentNumberLabel,
                id: 'consignmentNo',
                accessor: (d) => d.drugShipment.consignmentNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Depot Batch Number',
                id: 'depotBatchNo',
                accessor: (d) => d.depotBatch.batch.batchNo,
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Drug',
                id: 'drug',
                accessor: (d) => d.depotBatch.batch.drug.drugName,
                minWidth: 125,
                maxWidth: 125
            },
            {
                Header: 'Total',
                accessor: 'drugUnits',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Used',
                accessor: 'drugUnitsUsed',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Damaged',
                accessor: 'drugUnitsDamaged',
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Destroyed',
                id: 'totalDrugsDestroyed',
                accessor: (dsb) => <Typography color="error" variant="inherit"><strong>{dsb?.drugUnitsDestroyed ?? 0}</strong></Typography>,
                minWidth: 75,
                maxWidth: 75
            },
            {
                Header: 'Lost',
                accessor: 'drugUnitsLost',
                minWidth: 75,
                maxWidth: 75
            }
        ];

        return cols;
    }, []);

    return columns;
};

/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const AdministerPharmacy: React.FunctionComponent<IAdministerPharmacyProps> = () => {
    const classes = useStyles();

    const params = useParams<IAdministerPharmacyParams>();

    const [[canAdministerDrugManagement], ] = useBctApiPermissions(drugManagementPermissions);

    const [institutions, institutionsLoadState, institutionsActions] = useInstitutions();

    const [pharmacy, , , pharmacyActions] = usePharmacy();

    const [, quarantineStockActions] = useQuarantinePharmacyStock();
    const [, actionQuarantinedStockActions] = useActionQuarantinedPharmacyStock();

    const [, , pharmaciesActions] = usePharmacies();

    const [depots, depotsLoadState, depotsActions] = useDepots();

    const [drugShipmentDepotBatches, drugShipmentDepotBatchesLoadState, drugShipmentDepotBatchesActions] = useDrugShipmentDepotBatches();

    const [drugShipmentDepotBatch, drugShipmentDepotBatchLoadState, , drugShipmentDepotBatchActions] = useDrugShipmentDepotBatch();

    const [pharmacists, pharmacistsLoadState, pharmacistsActions] = usePharmacists();

    const [pharmacyForm, setPharmacyForm] = React.useState<Dtos.Pharmacy | null>(null);

    const [pharmacyModalOpen, setPharmacyModalOpen] = React.useState(false);

    const [quarantinePharmacyStockModalOpen, setQuarantinePharmacyStockModalOpen] = React.useState(false);

    const [actionQuarantinedStockModalOpen, setActionQuarantinedStockModalOpen] = React.useState(false);

    const [drugShipmentDepotBatchModalOpen, setDrugShipmentDepotBatchModalOpen] = React.useState(false);

    const [drugShipmentDepotBatchForm, setDrugShipmentDepotBatchForm] = React.useState<Dtos.DrugShipmentDepotBatch | null>(null);

    const [drugs, drugLoadState, drugActions] = useDrugs();

    const availableDepotBatches = React.useMemo(() => {
        return drugShipmentDepotBatches ?
            drugShipmentDepotBatches.filter(dsdb => dsdb.drugShipment.shipmentStatus === Dtos.ShipmentStatus.Available) :
            null;
    }, [drugShipmentDepotBatches]);

    const quarantinedAtSiteDepotBatches = React.useMemo(() => {
        return drugShipmentDepotBatches ?
            drugShipmentDepotBatches.filter(dsdb => dsdb.drugShipment.shipmentStatus === Dtos.ShipmentStatus.QuarantinedAtSite) :
            null;
    }, [drugShipmentDepotBatches]);

    const destroyedDepotBatches = React.useMemo(() => {
        return drugShipmentDepotBatches ?
            drugShipmentDepotBatches.filter(dsdb => dsdb.drugShipment.shipmentStatus === Dtos.ShipmentStatus.Destroyed) :
            null;
    }, [drugShipmentDepotBatches]);

    const getAvailablePharmacyDrug = React.useCallback((drugId: number) => {
        let drugTotal: number = 0;

        availableDepotBatches?.filter(x => x.depotBatch.batch.drugId === drugId)?.forEach((dsb) => {
            drugTotal += getDrugRemaining(dsb);
        });

        return drugTotal;
    }, [availableDepotBatches]);

    const batchSummary = React.useMemo(() => {
        if (availableDepotBatches &&
            availableDepotBatches.length > 0) {
            const groupedBatches: Map<number, IDrugShipmentDepotBatchSummary> = new Map();

            availableDepotBatches.forEach((dsdb) => {
                if (groupedBatches.has(dsdb.depotBatchId!)) {
                    let existingShipmentBatch = groupedBatches.get(dsdb.depotBatchId!);

                    if (existingShipmentBatch) {
                        existingShipmentBatch = sumDrugShipmentDepotBatches(existingShipmentBatch,
                            createDrugShipmentDepotBatchSummary(dsdb));

                        groupedBatches.set(dsdb.depotBatchId!, existingShipmentBatch);
                    }
                }
                else {
                    groupedBatches.set(dsdb.depotBatchId!, createDrugShipmentDepotBatchSummary(dsdb));
                }
            });

            return Array.from(groupedBatches.values());
        }
        else {
            return null;
        }
    }, [availableDepotBatches]);

    const availableDepotBatchColumns = useAvailableDepotBatchColumns(availableDepotBatches);
    const summedAvailableDepotBatchColumns = useSummedAvailableDepotBatchColumns(batchSummary);
    const quarantinedAtSiteDepotBatchColumns = useQuarantinedAtSiteDepotBatchColumns(quarantinedAtSiteDepotBatches);
    const destroyedDepotBatchColumns = useDestroyedDepotBatchColumns(destroyedDepotBatches);

    React.useEffect(() => {
        if (params.pharmacyId) {
            pharmacyActions.loadById(parseInt(params.pharmacyId, 10));
        }

        return () => {
            pharmacyActions.clear();
        };
    }, [params.pharmacyId, pharmacyActions]);

    React.useEffect(() => {
        institutionsActions.load();

        return () => {
            institutionsActions.clear();
        };
    }, [institutionsActions]);

    React.useEffect(() => {
        drugActions.load();

        return () => {
            drugActions.clear();
        };
    }, [drugActions]);

    React.useEffect(() => {
        depotsActions.load();

        return () => {
            depotsActions.clear();
        };
    }, [depotsActions]);

    React.useEffect(() => {
        pharmaciesActions.load();

        return () => {
            pharmaciesActions.clear();
        }
    }, [pharmaciesActions]);

    React.useEffect(() => {
        if (params.pharmacyId) {
            drugShipmentDepotBatchesActions.loadByPharmacyId(parseInt(params.pharmacyId, 10));
        }

        return () => {
            drugShipmentDepotBatchesActions.clear();
        }
    }, [params.pharmacyId, drugShipmentDepotBatchesActions]);

    React.useEffect(() => {
        if (pharmacy &&
            pharmacy.institutionCode) {
            pharmacistsActions.loadByInstCode(pharmacy.institutionCode);
        }

        return () => {
            pharmacistsActions.clear();
        }
    }, [pharmacy, pharmacistsActions]);

    React.useEffect(() => {
        if (drugShipmentDepotBatchLoadState.state === RequestState.Success) {
            setDrugShipmentDepotBatchForm(drugShipmentDepotBatch);

            setDrugShipmentDepotBatchModalOpen(true);
        }
    }, [drugShipmentDepotBatchLoadState.state, drugShipmentDepotBatch]);

    const onPharmacyFormSave = React.useCallback(() => {
        if (params.pharmacyId) {
            pharmacyActions.loadById(parseInt(params.pharmacyId, 10));
        }
    }, [params.pharmacyId, pharmacyActions]);

    const onDrugShipmentDepotBatchFormSave = React.useCallback(() => {
        drugShipmentDepotBatchActions.clear();

        if (params.pharmacyId) {
            drugShipmentDepotBatchesActions.loadByPharmacyId(parseInt(params.pharmacyId, 10));
        }
    }, [drugShipmentDepotBatchActions, params.pharmacyId, drugShipmentDepotBatchesActions]);

    const onQuarantinePharmacyStockSave = React.useCallback(() => {
        quarantineStockActions.clear();

        if (params.pharmacyId) {
            pharmacyActions.loadById(parseInt(params.pharmacyId, 10));
            drugShipmentDepotBatchesActions.loadByPharmacyId(parseInt(params.pharmacyId, 10));
        }
    }, [quarantineStockActions, drugShipmentDepotBatchesActions, params.pharmacyId, pharmacyActions]);

    const onActionQuarantinedStockSave = React.useCallback(() => {
        actionQuarantinedStockActions.clear();

        if (params.pharmacyId) {
            pharmacyActions.loadById(parseInt(params.pharmacyId, 10));
            drugShipmentDepotBatchesActions.loadByPharmacyId(parseInt(params.pharmacyId, 10));
        }
    }, [actionQuarantinedStockActions, pharmacyActions, params.pharmacyId, drugShipmentDepotBatchesActions]);

    const editPharmacyClick = React.useCallback(() => {
        setPharmacyForm(pharmacy);

        setPharmacyModalOpen(true);
    }, [pharmacy, setPharmacyModalOpen]);

    const quarantineStockClick = React.useCallback(() => {
        setQuarantinePharmacyStockModalOpen(true);
    }, [setQuarantinePharmacyStockModalOpen]);

    const actionQuarantinedStockClick = React.useCallback(() => {
        setActionQuarantinedStockModalOpen(true);
    }, [setActionQuarantinedStockModalOpen]);

    const onDrugShipmentDepotRowClick = React.useCallback((row: Row<Dtos.DrugShipmentDepotBatch>) => {
        if (!!canAdministerDrugManagement &&
            row &&
            row.original) {
            drugShipmentDepotBatchActions.clear();
            drugShipmentDepotBatchActions.loadById(row.original.id!);
        }
    }, [canAdministerDrugManagement, drugShipmentDepotBatchActions]);

    //this is here for general cleanup in case something is loaded into redux and never reset
    React.useEffect(() => {
        return () => {
            drugShipmentDepotBatchActions.clear();
            quarantineStockActions.clear();
        }
    }, [drugShipmentDepotBatchActions, quarantineStockActions]);

    return (
        <>
            <PharmacyModal
                pharmacists={pharmacists}
                pharmacistsLoadState={pharmacistsLoadState}
                depots={depots}
                depotsLoadState={depotsLoadState}
                drugs={drugs}
                drugLoadState={drugLoadState}
                institutionLoadState={institutionsLoadState}
                institutions={institutions}
                pharmacy={pharmacyForm}
                modalOpen={pharmacyModalOpen}
                setModalOpen={setPharmacyModalOpen}
                onFormSave={onPharmacyFormSave}
            />
            <DrugShipmentDepotBatchModal
                drugShipmentDepotBatch={drugShipmentDepotBatchForm}
                modalOpen={drugShipmentDepotBatchModalOpen}
                setModalOpen={setDrugShipmentDepotBatchModalOpen}
                onFormSave={onDrugShipmentDepotBatchFormSave}
            />
            {
                params.pharmacyId && (
                    <>
                        <QuarantinePharmacyStockModal
                            pharmacyId={parseInt(params.pharmacyId, 10)}
                            modalOpen={quarantinePharmacyStockModalOpen}
                            setModalOpen={setQuarantinePharmacyStockModalOpen}
                            onFormSave={onQuarantinePharmacyStockSave}
                        />
                        <EditQuarantinedStockModal
                            pharmacyId={parseInt(params.pharmacyId, 10)}
                            modalOpen={actionQuarantinedStockModalOpen}
                            setModalOpen={setActionQuarantinedStockModalOpen}
                            onFormSave={onActionQuarantinedStockSave}
                        />
                    </>
                )
            }
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                    institutionName={institutions?.find(i => i.code === pharmacy?.institutionCode)?.name}
                    institutionCode={pharmacy?.institutionCode}
                    showInstitution={true}
                    />
                }
                heading="Administer Pharmacy"
            >
                <Paper
                    className={classes.container}
                >
                    <PharmacyInformation
                        pharmacyName={pharmacy?.pharmacyName}
                        pharmacyStatus={pharmacy?.pharmacyStatus}
                        drugsInformation={
                            <>
                                {
                                    pharmacy?.pharmacyDrugs?.map((pd) => {
                                        return <div><small>{pd.drug.drugName} &mdash; {getAvailablePharmacyDrug(pd.drugId)}</small></div>
                                    })
                                }
                            </>
                        }
                    />
                    <div
                        className={classes.buttonSet}
                    >
                        {
                            !!canAdministerDrugManagement && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.button}
                                    onClick={quarantineStockClick}
                                    disabled={!availableDepotBatches || availableDepotBatches.length === 0}
                                >
                                    Quarantine Stock
                                </Button>
                            )
                        }
                        {
                            !!canAdministerDrugManagement && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.button}
                                    onClick={actionQuarantinedStockClick}
                                    disabled={!quarantinedAtSiteDepotBatches || quarantinedAtSiteDepotBatches.length === 0}
                                >
                                    Edit Stock in Quarantine
                                </Button>
                            )
                        }
                        {
                            !!canAdministerDrugManagement && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.button}
                                    onClick={editPharmacyClick}
                                >
                                    Edit Pharmacy
                                </Button>
                            )
                        }
                    </div>
                    <CollapsibleTable
                        title="Available Batch"
                        loadState={drugShipmentDepotBatchesLoadState}
                        data={batchSummary}
                        columns={summedAvailableDepotBatchColumns}
                        entityName="Batch"
                    />
                    <CollapsibleTable
                        title="Quarantined at Site Stock"
                        loadState={drugShipmentDepotBatchesLoadState}
                        data={quarantinedAtSiteDepotBatches}
                        columns={quarantinedAtSiteDepotBatchColumns}
                        titleFormattingOptions={{
                            plural: false,
                            lowercase: false
                        }}
                        noItemsFormattingOptions={{
                            plural: false,
                            lowercase: true
                        }}
                        entityName="Batch"
                    />
                    <CollapsibleTable
                        title="Received Shipment"
                        loadState={drugShipmentDepotBatchesLoadState}
                        data={availableDepotBatches}
                        columns={availableDepotBatchColumns}
                        onRowClick={onDrugShipmentDepotRowClick}
                        entityName="Batch"
                    />
                    <CollapsibleTable
                        title="Destroyed Batch"
                        loadState={drugShipmentDepotBatchesLoadState}
                        data={destroyedDepotBatches}
                        columns={destroyedDepotBatchColumns}
                        entityName="Batch"
                    />
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default AdministerPharmacy;